/**
 * PersonPicker - A reusable component for selecting persons
 * Supports both family tree persons and connected persons
 */

import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import { listTrees, listPersons } from "../../api/trees";
import { listConnectedPersons } from "../../api/connectedPersons";
import { useVaultContext } from "../../context/VaultContext";

export type PersonSelection = 
  | { type: "person"; id: number; name: string; treeName?: string }
  | { type: "connected_person"; id: number; name: string };

interface PersonPickerProps {
  /** Selected person IDs */
  selectedPersonIds?: number[];
  /** Selected connected person IDs */
  selectedConnectedPersonIds?: number[];
  /** Called when a person is added */
  onAddPerson?: (personId: number) => void;
  /** Called when a connected person is added */
  onAddConnectedPerson?: (connectedPersonId: number) => void;
  /** Called when a person is removed */
  onRemovePerson?: (personId: number) => void;
  /** Called when a connected person is removed */
  onRemoveConnectedPerson?: (connectedPersonId: number) => void;
  /** Show as multi-select with tags */
  multiple?: boolean;
  /** Single selection callback (for single mode) */
  onSelect?: (selection: PersonSelection | null) => void;
  /** Current single selection */
  value?: PersonSelection | null;
  placeholder?: string;
  className?: string;
}

interface CombinedPerson {
  type: "person" | "connected_person";
  id: number;
  name: string;
  treeName?: string;
  sex?: string;
  birthDate?: string;
}

export default function PersonPicker({
  selectedPersonIds = [],
  selectedConnectedPersonIds = [],
  onAddPerson,
  onAddConnectedPerson,
  onRemovePerson,
  onRemoveConnectedPerson,
  multiple = true,
  onSelect,
  value,
  placeholder = "Search and select people...",
  className = "",
}: PersonPickerProps) {
  const { getAccessTokenSilently } = useAuth0();
  const { currentVaultId } = useVaultContext();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const triggerRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
    openUpward: boolean;
  } | null>(null);

  // Fetch all trees (filtered by current vault)
  const { data: trees } = useQuery({
    queryKey: ["trees-for-picker", currentVaultId],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return listTrees(token, currentVaultId || undefined);
    },
    enabled: !!currentVaultId,
  });

  // Fetch persons from each tree
  const { data: allPersons, isLoading: personsLoading } = useQuery({
    queryKey: ["all-persons-for-picker", trees?.map((t) => t.id)],
    queryFn: async () => {
      if (!trees || trees.length === 0) return [];
      const token = await getAccessTokenSilently();
      const results: CombinedPerson[] = [];
      
      for (const tree of trees) {
        const persons = await listPersons(tree.id, token, { limit: 500 });
        for (const p of persons) {
          results.push({
            type: "person",
            id: p.id,
            name: p.full_name || `${p.given_name || ""} ${p.surname || ""}`.trim() || "Unknown",
            treeName: tree.name,
            sex: p.sex,
            birthDate: p.birth_date_text || undefined,
          });
        }
      }
      return results;
    },
    enabled: !!trees && trees.length > 0,
  });

  // Fetch connected persons
  const { data: connectedPersons, isLoading: connectedLoading } = useQuery({
    queryKey: ["connected-persons-for-picker"],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const cps = await listConnectedPersons(token, undefined, 500);
      return cps.map((cp): CombinedPerson => ({
        type: "connected_person",
        id: cp.id,
        name: cp.full_name || `${cp.given_name || ""} ${cp.surname || ""}`.trim() || "Unknown",
        sex: cp.sex || undefined,
        birthDate: cp.birth_date_text || undefined,
      }));
    },
  });

  // Combine and filter persons
  const filteredPersons = useMemo(() => {
    const all: CombinedPerson[] = [
      ...(allPersons || []),
      ...(connectedPersons || []),
    ];

    if (!search.trim()) return all;

    const searchLower = search.toLowerCase();
    return all.filter(
      (p) =>
        p.name.toLowerCase().includes(searchLower) ||
        p.treeName?.toLowerCase().includes(searchLower)
    );
  }, [allPersons, connectedPersons, search]);

  // Check if already selected
  const isSelected = (person: CombinedPerson) => {
    if (person.type === "person") {
      return selectedPersonIds.includes(person.id);
    } else {
      return selectedConnectedPersonIds.includes(person.id);
    }
  };

  // Get display name for selected persons
  const getSelectedDisplay = (): CombinedPerson[] => {
    const selected: CombinedPerson[] = [];
    
    for (const id of selectedPersonIds) {
      const person = allPersons?.find((p) => p.id === id);
      if (person) selected.push(person);
    }
    
    for (const id of selectedConnectedPersonIds) {
      const cp = connectedPersons?.find((p) => p.id === id);
      if (cp) selected.push(cp);
    }
    
    return selected;
  };

  const handleSelect = (person: CombinedPerson) => {
    if (multiple) {
      if (isSelected(person)) {
        // Remove
        if (person.type === "person") {
          onRemovePerson?.(person.id);
        } else {
          onRemoveConnectedPerson?.(person.id);
        }
      } else {
        // Add
        if (person.type === "person") {
          onAddPerson?.(person.id);
        } else {
          onAddConnectedPerson?.(person.id);
        }
      }
    } else {
      // Single select mode
      onSelect?.({
        type: person.type,
        id: person.id,
        name: person.name,
        treeName: person.treeName,
      });
      setIsOpen(false);
      setSearch("");
    }
  };

  const handleRemove = (person: CombinedPerson) => {
    if (person.type === "person") {
      onRemovePerson?.(person.id);
    } else {
      onRemoveConnectedPerson?.(person.id);
    }
  };

  const isLoading = personsLoading || connectedLoading;
  const selectedItems = getSelectedDisplay();

  // Calculate dropdown position
  const updateDropdownPosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownMaxHeight = 320; // Approximate max height of dropdown (max-h-64 + padding + done button)
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    
    // Determine if we should open upward (if not enough space below and more space above)
    const openUpward = spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow;

    setDropdownPosition({
      top: openUpward ? rect.top - 8 : rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      openUpward,
    });
  }, []);

  // Update position when dropdown opens
  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
    }
  }, [isOpen, updateDropdownPosition]);

  // Update position on scroll/resize
  useEffect(() => {
    if (!isOpen) return;

    const handleUpdate = () => {
      updateDropdownPosition();
    };

    window.addEventListener("scroll", handleUpdate, true); // Use capture to catch scroll in any container
    window.addEventListener("resize", handleUpdate);

    return () => {
      window.removeEventListener("scroll", handleUpdate, true);
      window.removeEventListener("resize", handleUpdate);
    };
  }, [isOpen, updateDropdownPosition]);

  return (
    <div className={`relative ${className}`}>
      {/* Trigger / Display */}
      <div
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
        className="min-h-[42px] flex flex-wrap items-center gap-1 w-full rounded border border-vault-200 px-3 py-2 bg-white cursor-pointer hover:border-vault-300"
      >
        {multiple && selectedItems.length > 0 ? (
          <>
            {selectedItems.map((person) => (
              <span
                key={`${person.type}-${person.id}`}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-sm ${
                  person.type === "person"
                    ? "bg-vault-100 text-vault-700"
                    : "bg-amber-100 text-amber-700"
                }`}
              >
                {person.name}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(person);
                  }}
                  className="hover:text-red-600"
                >
                  ×
                </button>
              </span>
            ))}
          </>
        ) : !multiple && value ? (
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${
              value.type === "person" ? "bg-vault-100 text-vault-600" : "bg-amber-100 text-amber-600"
            }`}>
              {value.type === "person" ? "Family" : "Connected"}
            </span>
            <span className="text-vault-900">{value.name}</span>
          </div>
        ) : (
          <span className="text-vault-400">{placeholder}</span>
        )}
        
        <div className="ml-auto flex items-center gap-1 flex-shrink-0">
          {!multiple && value && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSelect?.(null);
              }}
              className="p-1 text-vault-400 hover:text-vault-600"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 text-vault-400 transition-transform ${isOpen ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Dropdown and overlay (portaled to document.body) */}
      {isOpen && dropdownPosition && createPortal(
        <>
          {/* Click outside to close */}
          <div
            className="fixed inset-0 z-[100]"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown */}
          <div
            className="fixed z-[101] rounded-lg border border-vault-200 bg-white shadow-lg"
            style={{
              top: dropdownPosition.openUpward ? 'auto' : `${dropdownPosition.top}px`,
              bottom: dropdownPosition.openUpward ? `${window.innerHeight - dropdownPosition.top}px` : 'auto',
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
            }}
          >
            {/* Search input */}
            <div className="p-2 border-b border-vault-100">
              <input
                type="text"
                placeholder="Search by name or tree..."
                className="w-full rounded border border-vault-200 px-3 py-1.5 text-sm"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </div>

            {/* Person list */}
            <div className="max-h-64 overflow-y-auto">
              {isLoading ? (
                <div className="p-3 text-center text-vault-500 text-sm">Loading...</div>
              ) : filteredPersons.length > 0 ? (
                <>
                  {/* Family tree persons */}
                  {filteredPersons.filter((p) => p.type === "person").length > 0 && (
                    <>
                      <div className="px-3 py-1.5 text-xs font-semibold text-vault-500 bg-vault-50">
                        Family Tree Members
                      </div>
                      {filteredPersons
                        .filter((p) => p.type === "person")
                        .map((person) => (
                          <div
                            key={`person-${person.id}`}
                            onClick={() => handleSelect(person)}
                            className={`px-3 py-2 cursor-pointer hover:bg-vault-50 flex items-center gap-2 ${
                              isSelected(person) ? "bg-vault-100" : ""
                            }`}
                          >
                            {multiple && (
                              <input
                                type="checkbox"
                                checked={isSelected(person)}
                                readOnly
                                className="h-4 w-4 rounded border-vault-300 text-vault-600"
                              />
                            )}
                            <div className="h-8 w-8 rounded-full bg-vault-200 flex items-center justify-center flex-shrink-0">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-vault-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm text-vault-900 truncate">{person.name}</div>
                              <div className="text-xs text-vault-500 truncate">
                                {person.treeName}
                                {person.birthDate && ` · b. ${person.birthDate}`}
                              </div>
                            </div>
                          </div>
                        ))}
                    </>
                  )}

                  {/* Connected persons */}
                  {filteredPersons.filter((p) => p.type === "connected_person").length > 0 && (
                    <>
                      <div className="px-3 py-1.5 text-xs font-semibold text-amber-700 bg-amber-50">
                        Connected Persons
                      </div>
                      {filteredPersons
                        .filter((p) => p.type === "connected_person")
                        .map((person) => (
                          <div
                            key={`cp-${person.id}`}
                            onClick={() => handleSelect(person)}
                            className={`px-3 py-2 cursor-pointer hover:bg-vault-50 flex items-center gap-2 ${
                              isSelected(person) ? "bg-amber-100" : ""
                            }`}
                          >
                            {multiple && (
                              <input
                                type="checkbox"
                                checked={isSelected(person)}
                                readOnly
                                className="h-4 w-4 rounded border-vault-300 text-amber-600"
                              />
                            )}
                            <div className="h-8 w-8 rounded-full bg-amber-200 flex items-center justify-center flex-shrink-0">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm text-vault-900 truncate">{person.name}</div>
                              {person.birthDate && (
                                <div className="text-xs text-vault-500 truncate">
                                  b. {person.birthDate}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                    </>
                  )}
                </>
              ) : (
                <div className="p-3 text-center text-vault-500 text-sm">
                  {search ? "No people found" : "No people available"}
                </div>
              )}
            </div>

            {/* Done button for multi-select */}
            {multiple && (
              <div className="border-t border-vault-100 p-2">
                <button
                  onClick={() => setIsOpen(false)}
                  className="w-full rounded bg-vault-600 px-3 py-1.5 text-sm text-white hover:bg-vault-700"
                >
                  Done ({selectedItems.length} selected)
                </button>
              </div>
            )}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
